import { useQuery } from '@tanstack/react-query';
import type { DashboardStatsDto } from '@cpm/shared';
import { Layers, Package, Plug, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { DashboardMetricsPanel } from '../components/metrics-panels.js';
import { Badge, Card, CardHeader, ErrorNote, PageHeader, Spinner } from '../components/ui.js';

function StatCard({
  label,
  value,
  icon,
  to,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  to: string;
}) {
  return (
    <Link to={to}>
      <Card className="p-5 transition-shadow hover:shadow-md">
        <div className="flex items-center gap-4">
          <div className="flex size-11 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
            {icon}
          </div>
          <div>
            <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
            <p className="text-2xl font-semibold tabular-nums">{value}</p>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export function DashboardPage({ panelVersion }: { panelVersion: string }) {
  const stats = useQuery({
    queryKey: ['system', 'dashboard'],
    queryFn: () => api.get<DashboardStatsDto>('/api/system/dashboard'),
    refetchInterval: 60_000,
  });

  if (stats.isPending) return <Spinner />;
  if (stats.error) return <ErrorNote error={stats.error} />;
  const data = stats.data;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Overview of your hosting environment."
        actions={<Badge tone="gray">Panel v{panelVersion}</Badge>}
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Clients" value={data.clients} icon={<Users className="size-5" />} to="/clients" />
        <StatCard
          label="Active subscriptions"
          value={data.activeSubscriptions}
          icon={<Package className="size-5" />}
          to="/subscriptions"
        />
        <StatCard label="Service plans" value={data.plans} icon={<Layers className="size-5" />} to="/plans" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Coolify connection"
            actions={
              data.coolify.configured ? (
                data.coolify.reachable ? (
                  <Badge tone="green">Connected</Badge>
                ) : (
                  <Badge tone="red">Unreachable</Badge>
                )
              ) : (
                <Badge tone="yellow">Not configured</Badge>
              )
            }
          />
          <div className="px-5 py-4 text-sm">
            {data.coolify.configured ? (
              <dl className="space-y-2">
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500 dark:text-slate-400">Instance</dt>
                  <dd className="truncate font-medium">{data.coolify.baseUrl}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-slate-500 dark:text-slate-400">Version</dt>
                  <dd className="font-medium">{data.coolify.coolifyVersion ?? '—'}</dd>
                </div>
                {data.coolify.error && (
                  <div className="pt-1">
                    <ErrorNote error={new Error(data.coolify.error)} />
                  </div>
                )}
              </dl>
            ) : (
              <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                <Plug className="size-5 shrink-0" />
                <p>
                  Connect your Coolify instance under{' '}
                  <Link to="/settings" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
                    Settings
                  </Link>{' '}
                  to manage servers and provision projects.
                </p>
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Servers" description="Reported by Coolify" />
          <div className="px-5 py-4">
            {data.servers.length === 0 ? (
              <p className="py-4 text-sm text-slate-500 dark:text-slate-400">
                {data.coolify.reachable
                  ? 'No servers found.'
                  : 'Server list appears here once Coolify is connected.'}
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.servers.map((server) => (
                  <li key={server.uuid} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{server.name}</p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{server.ip}</p>
                    </div>
                    <Badge tone={server.reachable ? 'green' : 'red'}>
                      {server.reachable ? 'Reachable' : 'Unreachable'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>

      <DashboardMetricsPanel />
    </div>
  );
}
